import { ReactNode } from 'react'
import toast, { Toast } from 'react-hot-toast'
import classNames from 'classnames'

type MessageType = 'success' | 'error' | 'warning'

export const NotificationContent = ({
  t,
  type,
  children,
}: {
  t: Toast
  type: MessageType
  children: ReactNode
}) => {
  const borderColor = {
    success: 'border-brandGreen',
    error: 'border-brandOrange',
    warning: 'border-yellow-500',
  }[type]

  return (
    <div className={classNames(borderColor, 'border-b-4 mr-7 shadow-md p-8 bg-white min-w-72')}>
      <button
        onClick={() => {
          toast.dismiss(t.id)
        }}
        className="text-disabledFontColor text-xl mb-1 relative -top-8 -right-4 float-right"
      >
        x
      </button>
      {children}
    </div>
  )
}

type Error = {
  name: string
  statusText?: string
  message?: string
}

export const MessageNotification = ({
  t,
  type,
  children,
  title,
  message,
}: {
  t: Toast
  type: MessageType
  children?: ReactNode
  title?: string
  message?: string
}) => {
  return (
    <NotificationContent t={t} type={type}>
      <p className="font-semibold mb-3">{title}</p>
      <p>{message}</p>
      {children}
    </NotificationContent>
  )
}

const ErrorNotification = ({ error, t }: { error: unknown; t: Toast }) => {
  const castError = error as Error
  const title = castError.name == 'ApiError' ? 'Error:' : 'Sorry, an unexpected error has occurred:'
  const message =
    castError.name == 'ApiError'
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        ((error as any)?.body?.apierror?.message as string)
      : castError.name

  return (
    <MessageNotification type="error" title={title} message={message} t={t}>
      <p>
        <i>{castError.statusText || castError.message}</i>
      </p>
    </MessageNotification>
  )
}

const SuccessNotification = (props: { t: Toast; title?: string; message?: string }) => {
  return <MessageNotification type="success" {...props} />
}

const CustomErrorNotification = (props: { t: Toast; title?: string; message?: string }) => {
  return <MessageNotification type="error" {...props} />
}

const WarningNotification = (props: { t: Toast; title: string; message?: string }) => {
  return <MessageNotification type="warning" {...props} />
}

export const notification = {
  error: (error: unknown) => {
    toast.custom((t) => (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      <ErrorNotification error={error} t={t} />
    ))
  },
  warning: (title: string, message?: string) =>
    toast.custom((t) => <WarningNotification t={t} title={title} message={message} />),
  success: (title: string, message?: string) =>
    toast.custom((t) => <SuccessNotification t={t} title={title} message={message} />),
  CustomError: (title: string, message?: string) =>
    toast.custom((t) => <CustomErrorNotification t={t} title={title} message={message} />),
}
